/**
 * Cancel flow (#678) shared by the portfolio open-orders panel and the market page's "your open orders" list.
 *
 * One click = one signed L1 `cancel` action (several orders → one action, one signature). Same signing UX as the
 * ticket: chain 1337 switch, "Confirm in your wallet" dialog with the human list, 4001 → "Signature canceled".
 * Rows show "Canceling…" optimistically; the row disappears when openOrders / orderUpdates drop it (the
 * lifecycle tracker toasts "Order canceled"). A per-order error ("Order was never placed, already canceled, or
 * filled") clears the optimistic mark and shows on the row + as a toast.
 *
 * Guards: own wallet only (never in ghost mode), and mainnet cancels are off unless mainnetTradingEnabled()
 * (#684), exactly like orders. Cancels skip the ticket's first-mainnet-order confirmation: they only remove risk.
 *
 * 1-click trading (#688): with an approved agent the cancel is signed locally, so there is no wallet dialog. If
 * Hyperliquid no longer knows the agent, it is forgotten and the cancel falls back to the wallet (dialog shown).
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Hex } from 'viem';
import { formatPx, labelCoin, useLabelIndex, type LabelIndex } from '../account/orders/labels';
import { useAgent } from '../agent/context';
import { toasts } from '../components/toastStore';
import { useEnv } from '../config/env';
import type { BasicOrder } from '../hl/types';
import { useViewedAccount, useWallet } from '../wallet/context';
import { buildCancelAction, cancelErrorText, isCancellableCoin, signAndSubmitCancel } from './cancel';
import { mainnetTradingEnabled } from './order';
import { WalletConfirmModal } from './WalletConfirm';
import { t } from '../i18n/i18n';
import { localizeNumber } from '../i18n/format';
import { networkWord } from '../i18n/common';
import { useT } from '../i18n/useT';

export type CancelOrderRef = Pick<BasicOrder, 'coin' | 'oid' | 'side' | 'limitPx' | 'sz'>;

export interface CancelRowState {
  canceling: boolean;
  error?: string;
}

export interface CancelControls {
  /** Cancel is possible at all (own connected wallet, not ghost). Rows hide the button otherwise. */
  available: boolean;
  /** Set when the button should show but stay disabled (e.g. mainnet trading off). */
  disabledReason: string | null;
  cancel: (orders: readonly CancelOrderRef[]) => void;
  rowState: (oid: number) => CancelRowState | undefined;
  /** Rows of other markets (spot / perps) cannot be canceled here. */
  canCancelCoin: (coin: string) => boolean;
  busy: boolean;
  notice: string | null;
  /** The wallet-confirm dialog; render it once next to the list. */
  dialog: ReactNode;
}

/** If a successful cancel's row has not disappeared after this long, drop the "Canceling…" mark. */
const STALE_CANCELING_MS = 20_000;

export function describeOrder(o: CancelOrderRef, index: LabelIndex): string {
  const l = labelCoin(o.coin, index);
  return t('orders.toast.describe', {
    action: t(o.side === 'B' ? 'ticket.buy' : 'ticket.sell'),
    sz: localizeNumber(String(Number(o.sz))),
    label: l.label,
    px: formatPx(o.limitPx, l.kind),
  });
}

export function useCancelOrders(): CancelControls {
  useT();
  const { env } = useEnv();
  const wallet = useWallet();
  const agent = useAgent();
  const viewed = useViewedAccount();
  const index = useLabelIndex();
  const own = wallet.status === 'connected' ? wallet.address : null;
  const available = viewed.canTrade && !!own && !!wallet.provider;
  const disabledReason = env.isMainnet && !mainnetTradingEnabled() ? t('common.mainnet_off') : null;

  const [rows, setRows] = useState<Map<number, CancelRowState>>(() => new Map());
  const [signing, setSigning] = useState<readonly CancelOrderRef[] | null>(null);
  /** The in-flight cancel is signed by the wallet (show the confirm dialog), not by the agent. */
  const [viaWallet, setViaWallet] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const t = timers.current;
    return () => {
      for (const h of t) clearTimeout(h);
      t.clear();
    };
  }, []);

  const patch = useCallback((oids: readonly number[], st: CancelRowState | null) => {
    setRows((prev) => {
      const next = new Map(prev);
      for (const oid of oids) {
        if (st) next.set(oid, st);
        else next.delete(oid);
      }
      return next;
    });
  }, []);

  const cancel = useCallback(
    (orders: readonly CancelOrderRef[]) => {
      const targets = orders.filter((o) => isCancellableCoin(o.coin));
      if (!available || disabledReason || signing || targets.length === 0 || !wallet.provider || !own) return;
      let action;
      try {
        action = buildCancelAction(targets);
      } catch (e) {
        setNotice(e instanceof Error ? e.message : String(e));
        return;
      }
      const oids = targets.map((o) => o.oid);
      setNotice(null);
      patch(oids, { canceling: true });
      setSigning(targets);
      const signer = agent.signer;
      setViaWallet(!signer);
      const user = own.toLowerCase();
      void signAndSubmitCancel(action, {
        provider: wallet.provider,
        account: own as Hex,
        env,
        walletRdns: wallet.walletInfo?.rdns ?? null,
        agent: signer,
        onAgentMissing: () => {
          agent.forget(t('ticket.agent_gone'));
          setViaWallet(true);
        },
      })
        .then((res) => {
          if (res.kind === 'rejected') {
            patch(oids, null);
            setNotice(`${t('cancel.signature_canceled')}${res.hint ? ` ${res.hint}` : ''}`);
            return;
          }
          if (res.kind === 'err') {
            patch(oids, { canceling: false, error: res.message });
            toasts.push({ tone: 'error', title: t('cancel.failed'), body: res.message });
            return;
          }
          targets.forEach((o, i) => {
            const st = res.statuses[i] ?? { kind: 'error' as const, message: t('cancel.no_status') };
            if (st.kind === 'success') {
              const h = setTimeout(() => {
                timers.current.delete(h);
                setRows((prev) => {
                  if (!prev.get(o.oid)?.canceling) return prev;
                  const next = new Map(prev);
                  next.delete(o.oid);
                  return next;
                });
              }, STALE_CANCELING_MS);
              timers.current.add(h);
              return;
            }
            const msg = cancelErrorText(st.message);
            patch([o.oid], { canceling: false, error: msg });
            toasts.push({
              key: `${user}:${o.oid}:cancel-error:${Date.now()}`,
              tone: 'error',
              title: t('cancel.failed'),
              body: `${describeOrder(o, index)} · ${msg}`,
            });
          });
        })
        .finally(() => setSigning(null));
    },
    [available, disabledReason, signing, wallet.provider, wallet.walletInfo, own, env, patch, index, agent],
  );

  const rowState = useCallback((oid: number) => rows.get(oid), [rows]);

  const dialog = (
    <WalletConfirmModal open={signing !== null && viaWallet} what="cancel">
      {signing && (
        <dl className="space-y-1 text-sm" data-testid="cancel-summary">
          <div className="flex justify-between">
            <dt className="text-muted">{t('ticket.summary.network')}</dt>
            <dd className="font-medium">{networkWord(env.name)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted">{t('ticket.summary.action')}</dt>
            <dd className="font-semibold text-danger">{t('orders.cancel.summary_action', { count: signing.length })}</dd>
          </div>
          <div>
            <ul className="space-y-0.5 rounded-md bg-surface-2/50 p-2 text-xs">
              {signing.map((o) => (
                <li key={o.oid} className="num">
                  {describeOrder(o, index)} <span className="text-muted">{t('ticket.status.oid_suffix', { oid: String(o.oid) })}</span>
                </li>
              ))}
            </ul>
          </div>
        </dl>
      )}
    </WalletConfirmModal>
  );

  return {
    available,
    disabledReason,
    cancel,
    rowState,
    canCancelCoin: isCancellableCoin,
    busy: signing !== null,
    notice,
    dialog,
  };
}
